// Weather transport and display helpers.
// Forecast proxy returns structured Open-Meteo GFS/HRRR fields plus precipitation history.
//
// Request ownership, cache lifetime, and shared subscriber state live in
// app/externalSources/weatherForecastSource.ts. This module is intentionally a
// stateless transport: weather is external HTTP, not a routeDataCatalog or
// Supabase resource.

const FORECAST_URL = '/.netlify/functions/weather-forecast';
export const WEATHER_FORECAST_CACHE_MS = 15 * 60 * 1000;

// The forecast-proxy payload. Only the two fields this module itself reads are
// named — `error`, which the proxy sets on a degraded upstream instead of
// failing the request, and `radarUrl`, which officialRadarUrl provides a
// fallback for. Everything else (current / today / daily / hourly /
// rainWindows / monthlyPrecip) is passed through untouched to WeatherPage and
// HeaderWeatherChip, which own their own reads. Restating that structure here
// would be a second, drifting copy of netlify/functions/weather-forecast.ts.
export type WeatherForecast = {
  error?: unknown;
  radarUrl?: string | null;
  [field: string]: unknown;
};

/**
 * Fetch one forecast payload. This owns no cache and accepts no AbortSignal:
 * the shared external source owns one request independently of whichever UI
 * component is currently mounted.
 */
export async function fetchWeatherForecast({force = false}: {force?: boolean} = {}): Promise<WeatherForecast | null> {
  const url = force ? `${FORECAST_URL}?_t=${Date.now()}` : FORECAST_URL;
  const res = await fetch(url);
  if (!res.ok) return null;
  // res.json() is declared to resolve to `any`; the annotation contains it at
  // the transport boundary rather than letting it spread through consumers.
  const data: WeatherForecast = await res.json();
  return data.error ? null : data;
}

export function officialRadarUrl(forecast: WeatherForecast | null | undefined): string {
  return forecast?.radarUrl || 'https://radar.weather.gov/';
}

// Open-Meteo/Tomorrow.io condition codes. Both maps are partial by design — an
// unrecognised code falls back rather than throwing — so the lookups below
// return `string | undefined` and each has its own fallback.
const WEATHER_CODES: Record<number, string | undefined> = {
  0: 'Unknown',
  1000: 'Clear',
  1100: 'Mostly Clear',
  1101: 'Partly Cloudy',
  1102: 'Mostly Cloudy',
  1001: 'Cloudy',
  2000: 'Fog',
  2100: 'Light Fog',
  4000: 'Drizzle',
  4001: 'Rain',
  4200: 'Light Rain',
  4201: 'Heavy Rain',
  5000: 'Snow',
  5001: 'Flurries',
  5100: 'Light Snow',
  5101: 'Heavy Snow',
  6000: 'Freezing Drizzle',
  6001: 'Freezing Rain',
  6200: 'Lt Freezing Rain',
  6201: 'Hvy Freezing Rain',
  7000: 'Ice Pellets',
  7101: 'Heavy Ice Pellets',
  7102: 'Light Ice Pellets',
  8000: 'Thunderstorm',
};

export function weatherLabel(code: number): string {
  return WEATHER_CODES[code] || '';
}

const WEATHER_ICONS: Record<number, string | undefined> = {
  1000: '☀️',
  1100: '🌤️',
  1101: '⛅',
  1102: '🌥️',
  1001: '☁️',
  2000: '🌫️',
  2100: '🌫️',
  4000: '🌧️',
  4001: '🌧️',
  4200: '🌦️',
  4201: '🌧️',
  5000: '🌨️',
  5001: '🌨️',
  5100: '🌨️',
  5101: '🌨️',
  6000: '🧊',
  6001: '🧊',
  6200: '🧊',
  6201: '🧊',
  7000: '🧊',
  7101: '🧊',
  7102: '🧊',
  8000: '⛈️',
};

export function weatherIcon(code: number): string {
  return WEATHER_ICONS[code] || '☁️';
}

// The line icon for a forecast code (Design Constitution A21, T01: no emoji in
// the UI). Same grouping as WEATHER_ICONS; unknown codes read as cloud.
export type WeatherIconName = 'sun' | 'cloudSun' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm';

export function weatherIconName(code: number): WeatherIconName {
  if (code === 1000) return 'sun';
  if (code === 1100 || code === 1101) return 'cloudSun';
  if (code === 2000 || code === 2100) return 'fog';
  if (code >= 4000 && code < 5000) return 'rain';
  if (code >= 5000 && code < 8000) return 'snow'; // snow, freezing rain, ice pellets
  if (code === 8000) return 'storm';
  return 'cloud';
}
